// Picker for the budget classifications (Ministerul Finanțelor): the functional code of the local budgets (capitol / subcapitol /
// paragraf, "51.02.01.03") and the economic code (titlu / articol / alineat, "20.01.01"). One searchable list, grouped by the budget's
// parts (functional) or titles (economic), the codes already used in the budget first; only codes from the list can be chosen.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { Floating, norm } from './index';
import { Icon } from './Icon';

export interface ClassCode { code: string; name: string; level: number; group: string; custom?: boolean; used?: boolean }
export interface Classification { functional: ClassCode[]; economic: ClassCode[]; source?: string; econ?: { code: string; name: string }[]; sources?: { code: string; name: string }[]; sectors?: { code: string; name: string }[] }

/** Which codes a budget line can use: a chapter / subchapter / paragraph (not a part header); an article / paragraph (not a whole title). */
export const selectable = (kind: 'F' | 'E', c: ClassCode) => (kind === 'F' ? c.level >= 1 : c.level >= 2);
/** "51.2.1.3", "51 02 01 03", "51020103" → "51.02.01.03" (as the server does). */
export function normalizeCode(s: string): string {
  const t = (s || '').trim().replace(/,/g, '.');
  let p = t.split(/[.\s]+/).filter(Boolean);
  if (p.length === 1 && /^\d{4,8}$/.test(p[0]) && p[0].length % 2 === 0) p = p[0].match(/../g) || p;
  return p.every(x => /^\d{1,2}$/.test(x)) ? p.map(x => x.padStart(2, '0')).join('.') : t;
}
const GROUP_LABEL = (kind: 'F' | 'E', g: string) => kind === 'F' ? g.replace(/^Partea\s+/i, 'Partea ').replace(/\s+([A-ZĂÂÎȘȚ ,/]{6,})$/, (_, x) => ' — ' + x.charAt(0) + x.slice(1).toLowerCase()) : g.replace(/^(TITLUL|Titlul)\s+([IVXLC]+)\s+/, 'Titlul $2 — ').replace(/—\s+([A-ZĂÂÎȘȚ ,()/-]{6,})$/, (_, x) => '— ' + x.charAt(0) + x.slice(1).toLowerCase());

type Row = { kind: 'head'; label: string; key: string } | { kind: 'item'; c: ClassCode; key: string; pickable: boolean };

export function CodePicker({ kind, codes, value, onChange, placeholder, invalid, autoFocus }: {
  kind: 'F' | 'E'; codes: ClassCode[] | null; value: string; onChange: (code: string, c: ClassCode | null) => void; placeholder?: string; invalid?: boolean; autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [hl, setHl] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const all = codes || [];
  const current = all.find(c => c.code === normalizeCode(value || ''));
  const unknown = !!value && !current;

  const rows: Row[] = useMemo(() => {
    const q = norm(text), digits = q.replace(/[^\d]/g, ''), words = q.split(/\s+/).filter(w => w && !/^[\d.]+$/.test(w));
    const match = (c: ClassCode) => {
      if (!q) return true;
      const plain = c.code.replace(/\./g, '');
      const codeOk = !digits || plain.startsWith(digits) || c.code.startsWith(q);
      const nameOk = words.every(w => norm(c.name).includes(w) || norm(c.group).includes(w));
      return codeOk && nameOk && (!!digits || words.length > 0);
    };
    const out: Row[] = [];
    if (!q) {
      const used = all.filter(c => c.used && selectable(kind, c));
      if (used.length) { out.push({ kind: 'head', label: 'Folosite în bugetul dumneavoastră', key: 'h-used' }); used.forEach(c => out.push({ kind: 'item', c, key: 'u' + c.code, pickable: true })); }
    }
    let group: string | null = null;
    for (const c of all) {
      if (!match(c)) continue;
      if ((kind === 'F' && c.level === 0) || (kind === 'E' && c.level === 1)) { if (c.group !== group) { group = c.group; out.push({ kind: 'head', label: GROUP_LABEL(kind, c.group || c.name), key: 'h' + c.code }); } continue; }
      if (c.group !== group) { group = c.group; out.push({ kind: 'head', label: c.group ? GROUP_LABEL(kind, c.group) : '—', key: 'h' + c.code }); }
      out.push({ kind: 'item', c, key: c.code, pickable: selectable(kind, c) });
    }
    return out;
  }, [text, all, kind]);
  const pickables = rows.map((r, i) => (r.kind === 'item' && r.pickable ? i : -1)).filter(i => i >= 0);
  const hlRow = pickables[Math.min(hl, pickables.length - 1)] ?? -1;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (ref.current && !ref.current.contains(t) && !(pop.current && pop.current.contains(t))) close(); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => { if (open) listRef.current?.querySelector('.cp-item.hl')?.scrollIntoView({ block: 'nearest' }); }, [hl, open, text]);

  const openIt = () => { setText(''); setHl(0); setOpen(true); };
  const close = () => { setOpen(false); setText(''); };
  const pick = (c: ClassCode) => { onChange(c.code, c); close(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { openIt(); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { setHl(h => Math.min(pickables.length - 1, h + 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setHl(h => Math.max(0, h - 1)); e.preventDefault(); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = rows[hlRow]; if (r && r.kind === 'item') pick(r.c); }
    else if (e.key === 'Escape' || e.key === 'Tab') close();
  };
  const display = open ? text : current ? `${current.code} — ${current.name}` : value || '';

  return (
    <div className={'combo cp' + (unknown ? ' unknown' : '')} ref={ref}>
      <input className={'input ' + (invalid || unknown ? 'invalid' : '')} value={display} autoFocus={autoFocus} autoComplete="off" role="combobox" aria-expanded={open}
        placeholder={placeholder || (kind === 'F' ? 'Căutați după cod sau cuvânt, ex. 84 sau iluminat' : 'Căutați după cod sau cuvânt, ex. 20.01 sau carburanți')}
        onFocus={openIt} onClick={() => { if (!open) openIt(); }} onChange={e => { setText(e.target.value); setHl(0); if (!open) setOpen(true); }} onKeyDown={onKey} />
      <Icon name="chevronDown" className="caret" size={16} />
      {open && (
        <Floating anchor={ref} open={open} className="popover cp-pop" innerRef={pop} maxH={380} width={Math.max(ref.current?.getBoundingClientRect().width || 0, 560)}>
          <div ref={listRef}>
            {codes == null && <div className="none">Se încarcă clasificația…</div>}
            {codes != null && pickables.length === 0 && <div className="none">Niciun cod nu corespunde. Căutați după cifre (ex. {kind === 'F' ? '70.02' : '20.01'}) sau după cuvinte din denumire.</div>}
            {rows.map((r, i) => r.kind === 'head'
              ? <div key={r.key} className="cp-head">{r.label}</div>
              : <div key={r.key} className={'cp-item lv' + r.c.level + (r.pickable ? '' : ' group') + (i === hlRow ? ' hl' : '') + (r.c.code === current?.code ? ' sel' : '')} role="option" aria-selected={r.c.code === current?.code}
                  onMouseEnter={() => { if (r.pickable) setHl(pickables.indexOf(i)); }} onMouseDown={e => { e.preventDefault(); if (r.pickable) pick(r.c); }}>
                  <span className="cp-code">{r.c.code}</span><span className="cp-name" style={{ paddingLeft: Math.max(0, r.c.level - (kind === 'F' ? 1 : 2)) * 14 }}>{r.c.name}</span>
                  {r.c.custom && <span className="cp-tag">adăugat</span>}
                  {r.c.code === current?.code && <Icon name="check" size={14} />}
                </div>)}
          </div>
        </Floating>
      )}
      {unknown && !open && <div className="cp-warn"><Icon name="alert" size={13} />Codul nu este în clasificația oficială — alegeți-l din listă.</div>}
    </div>
  );
}

/** The classifications from the ALOP service, loaded once per page (they change once a year). */
let cache: Promise<Classification> | null = null;
export function useClassification(): Classification | null {
  const [c, setC] = useState<Classification | null>(null);
  useEffect(() => {
    if (!cache) cache = api.get<Classification>('/api/alop/classification').catch(e => { cache = null; throw e; });
    cache.then(setC).catch(() => setC({ functional: [], economic: [] }));
  }, []);
  return c;
}
/** After an administrator adds a code: the next picker reloads the lists. */
export const resetClassification = () => { cache = null; };
